import {
  Bdkv2OAuthFlow,
  type Bdkv2BookDescriptor,
} from '@/generated/hooks/bdk/models';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  cn,
  Field,
  FieldContent,
  FieldLabel,
  FieldTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
  Separator,
  Text,
} from '@kognitos/lattice';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  createAuthenticationMethodStepSchema,
  type AuthenticationMethodStepData,
} from '../schemas';
import {
  authRequiresNoCredentials,
  normalizeAuthenticationMethods,
  shouldShowDirectOAuthButton,
} from '../utils';
import { OAuthSignInButton } from './OAuthSignInButton';

export interface ISelectAuthenticationMethodStepProps {
  integration: Bdkv2BookDescriptor;
  defaultValues?: Partial<AuthenticationMethodStepData>;
  onSubmit: (data: AuthenticationMethodStepData) => void;
  existingConnectionNames?: string[];
  /**
   * Under a "Sign in with …" button backed by a platform-provided app: sign in
   * through the organization's own OAuth app instead. Shown for the methods
   * `canUseOwnOAuthApp` accepts.
   */
  onUseOwnOAuthApp?: (authMethodId: string, connectionName: string) => void;
  canUseOwnOAuthApp?: (authMethodId: string) => boolean;
  onOAuthClick?: (
    authMethodId: string,
    connectionName: string,
  ) => Promise<void>;
}

const DEFAULT_EXISTING_CONNECTION_NAMES: string[] = [];

export function SelectAuthenticationMethodStep({
  integration,
  defaultValues,
  onSubmit,
  existingConnectionNames = DEFAULT_EXISTING_CONNECTION_NAMES,
  onUseOwnOAuthApp,
  canUseOwnOAuthApp,
  onOAuthClick,
}: ISelectAuthenticationMethodStepProps) {
  const { t: tIntegrations } = useTranslation('integrations');
  const authenticationMethods = normalizeAuthenticationMethods(integration);

  // Group authentication methods by preferred status
  const preferredMethods = authenticationMethods.filter(
    auth => auth.oauth?.preferred || auth.custom?.preferred,
  );
  // Two preferred (managed) methods that share a provider render identical
  // "Sign in with <Provider>" buttons. Count preferred methods per provider so
  // we only fall back to the connection's own display name when a provider is
  // duplicated; methods for different providers keep the clearer default label.
  const preferredCountByProvider = preferredMethods.reduce<
    Record<string, number>
  >((acc, auth) => {
    const providerKey = auth.oauth?.provider ?? auth.custom?.id ?? '';
    acc[providerKey] = (acc[providerKey] ?? 0) + 1;
    return acc;
  }, {});
  // Extract OAuth authorization code method and the rest by checking flows array
  const oAuthAuthorizationCodeMethod = authenticationMethods.find(auth =>
    auth.oauth?.flows?.includes(Bdkv2OAuthFlow.OAuthFlowAuthorizationCode),
  );
  const otherMethods = authenticationMethods.filter(
    auth => !auth.oauth?.preferred && !auth.custom?.preferred,
  );

  const step1Schema = createAuthenticationMethodStepSchema(
    existingConnectionNames,
  );

  // Pre-select auth method if there's only one option
  const defaultAuthMethodId =
    authenticationMethods.length === 1
      ? authenticationMethods[0].custom?.id ||
        authenticationMethods[0].oauth?.id ||
        ''
      : '';

  const form = useForm<AuthenticationMethodStepData>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      connectionName: '',
      authMethodId: defaultAuthMethodId,
      createSameConnection: false,
      ...defaultValues,
    },
  });

  // Watch auth method to auto-check createSameConnection for methods with no credentials
  const authMethodId = form.watch('authMethodId');
  // Check if selected auth method is OAuth authorization code by comparing with the method's id
  const isOAuthAuthCode =
    authMethodId === oAuthAuthorizationCodeMethod?.oauth?.id;

  // Find the selected authentication method
  const selectedAuthMethod = authenticationMethods.find(
    auth => (auth.custom?.id || auth.oauth?.id) === authMethodId,
  );

  // Check if selected method requires no credentials
  const selectedMethodHasNoCredentials = selectedAuthMethod
    ? authRequiresNoCredentials(selectedAuthMethod)
    : false;

  // Auto-check createSameConnection when OAuth auth code or no-credential auth is selected
  React.useEffect(() => {
    if (isOAuthAuthCode || selectedMethodHasNoCredentials) {
      form.setValue('createSameConnection', true);
    }
  }, [isOAuthAuthCode, selectedMethodHasNoCredentials, form]);

  const handleSubmit = form.handleSubmit(
    (data: AuthenticationMethodStepData) => {
      onSubmit(data);
    },
  );

  const getRadioItemClassName = () =>
    cn('cursor-pointer transition-colors duration-200 !rounded-lg');

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="space-y-6"
        id="connection-name-and-auth-method-form"
      >
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="connectionName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {tIntegrations(
                    'integrations.addConnection.connectionName.label',
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={tIntegrations(
                      'integrations.addConnection.connectionName.placeholder',
                    )}
                    className="w-full"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {tIntegrations(
                    'integrations.addConnection.connectionName.description',
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Create Same Connection Checkbox */}
          {authenticationMethods.length > 0 && (
            <FormField
              control={form.control}
              name="createSameConnection"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={
                        isOAuthAuthCode || selectedMethodHasNoCredentials
                      }
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="cursor-pointer">
                      {tIntegrations(
                        'integrations.addConnection.createSameConnection.label',
                      )}
                    </FormLabel>
                    <FormDescription>
                      {tIntegrations(
                        'integrations.addConnection.createSameConnection.description',
                      )}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          )}

          {authenticationMethods.length > 0 && (
            <FormField
              control={form.control}
              name="authMethodId"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-col gap-4">
                      {/* Preferred Methods Section */}
                      {preferredMethods.length > 0 && (
                        <div className="space-y-3">
                          <Text weight="medium" level="small">
                            {tIntegrations(
                              'integrations.addConnection.authenticationMethod.preferred',
                            )}
                          </Text>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex flex-col gap-2"
                          >
                            {preferredMethods.map(auth => {
                              const authId = auth.custom?.id || auth.oauth?.id;
                              const authName =
                                auth.custom?.name || auth.oauth?.name || authId;

                              // Show direct OAuth button if conditions match
                              if (shouldShowDirectOAuthButton(auth)) {
                                return (
                                  <React.Fragment key={authId}>
                                    <OAuthSignInButton
                                      integration={integration}
                                      authentication={auth}
                                      displayName={
                                        (preferredCountByProvider[
                                          auth.oauth?.provider ??
                                            auth.custom?.id ??
                                            ''
                                        ] ?? 0) > 1
                                          ? auth.oauth?.name ||
                                            auth.custom?.name
                                          : undefined
                                      }
                                      disabled={!form.watch('connectionName')}
                                      onClick={async () => {
                                        // Only validate connection name for direct OAuth flows
                                        const isValid =
                                          await form.trigger('connectionName');
                                        if (isValid) {
                                          // Don't set form fields - just call OAuth handler directly
                                          // This prevents form submission and wizard advancement
                                          await onOAuthClick?.(
                                            authId || '',
                                            form.watch('connectionName'),
                                          );
                                        }
                                      }}
                                    />
                                    {onUseOwnOAuthApp &&
                                      canUseOwnOAuthApp?.(authId || '') && (
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className="self-center text-muted-foreground"
                                          disabled={
                                            !form.watch('connectionName')
                                          }
                                          onClick={async () => {
                                            if (
                                              await form.trigger(
                                                'connectionName',
                                              )
                                            ) {
                                              onUseOwnOAuthApp(
                                                authId || '',
                                                form.watch('connectionName'),
                                              );
                                            }
                                          }}
                                        >
                                          {tIntegrations(
                                            'integrations.addConnection.useOwnOAuthApp',
                                          )}
                                        </Button>
                                      )}
                                  </React.Fragment>
                                );
                              }

                              // Otherwise render as radio option
                              return (
                                <FieldLabel
                                  key={authId}
                                  htmlFor={authId}
                                  className={getRadioItemClassName()}
                                >
                                  <Field
                                    orientation="horizontal"
                                    className="!p-3 !items-center gap-2"
                                  >
                                    <RadioGroupItem
                                      value={authId || ''}
                                      id={authId}
                                      className={cn('cursor-pointer')}
                                    />
                                    <FieldContent>
                                      <FieldTitle className="!font-normal !leading-none">
                                        {authName}
                                      </FieldTitle>
                                    </FieldContent>
                                  </Field>
                                </FieldLabel>
                              );
                            })}
                          </RadioGroup>
                        </div>
                      )}

                      {/* Separator between sections */}
                      {preferredMethods.length > 0 &&
                        otherMethods.length > 0 && <Separator />}

                      {/* Other Methods Section */}
                      {otherMethods.length > 0 && (
                        <div className="space-y-3">
                          {preferredMethods.length > 0 && (
                            <Text weight="medium" level="small">
                              {tIntegrations(
                                'integrations.addConnection.authenticationMethod.otherOptions',
                              )}
                            </Text>
                          )}
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex flex-col gap-2"
                          >
                            {otherMethods.map(auth => {
                              const authId = auth.custom?.id || auth.oauth?.id;
                              const authName =
                                auth.custom?.name || auth.oauth?.name || authId;
                              return (
                                <FieldLabel
                                  key={authId}
                                  htmlFor={authId}
                                  className={getRadioItemClassName()}
                                >
                                  <Field
                                    orientation="horizontal"
                                    className="!p-3 !items-center gap-2"
                                  >
                                    <RadioGroupItem
                                      value={authId || ''}
                                      id={authId}
                                      className={cn('cursor-pointer')}
                                    />
                                    <FieldContent>
                                      <FieldTitle className="!font-normal !leading-none">
                                        {authName}
                                      </FieldTitle>
                                    </FieldContent>
                                  </Field>
                                </FieldLabel>
                              );
                            })}
                          </RadioGroup>
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </form>
    </Form>
  );
}
